/**
 * DeviceLocationPhotos — the device sheet's "Location & photos" section.
 *
 * Owner request 2026-09-14, after an AP "behind the basement TV" could not be found: every
 * device gets an installer NOTE and up to THREE PHOTOS of where and how it is mounted.
 *
 * Built for the person standing next to the device with a tablet or phone:
 *  - the note is an always-open textarea (no "Edit" tap first) with an explicit Save;
 *  - three fixed slots, so capacity reads at a glance ("2 of 3") before anyone tries a 4th;
 *  - the "Add photo" slot opens a chooser: "Take photo" is an in-app LIVE camera with a
 *    review/retake step (DeviceCameraCapture); "Choose file" is the original file input
 *    (capture="environment"), kept as the fallback for older browsers / no camera / denied;
 *  - either way the photo is downscaled + re-encoded in the browser first (lib/devicePhotoPrep), with
 *    visible preparing/upload progress and a plain-sentence error;
 *  - tap a thumbnail for the full-size viewer, where the caption is edited and a photo is
 *    deleted behind an inline confirm.
 *
 * Roles: site_operator+ gets the write controls (the server enforces the same floor with
 * @require("site_operator") in api/v2/device_photos.py). read_only sees the note and the
 * photos and nothing that changes them.
 *
 * Colour: brand violet only on the interactive affordances (Save, Add photo, focus); the
 * status ramp is not used — a photo is not an operational state. Errors use the critical ink,
 * as the rest of the device sheet does.
 */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/Button";
import { DeviceCameraCapture } from "@/components/device/DeviceCameraCapture";
import { ApiError } from "@/lib/api/client";
import { cameraCaptureSupported } from "@/lib/deviceCamera";
import {
  useDeleteDevicePhoto,
  useDevicePhotos,
  useSetDeviceDescription,
  useSetPhotoCaption,
  useUploadDevicePhoto,
} from "@/lib/api/hooks";
import {
  CAPTION_MAX,
  DESCRIPTION_MAX,
  MAX_PHOTOS,
  charsLeft,
  formatBytes,
  precheckPhotoFile,
  progressPercent,
  reencodedName,
  slotCountLabel,
  uploadErrorMessage,
  uploadTooLarge,
} from "@/lib/devicePhotos";
import { prepareForUpload } from "@/lib/devicePhotoPrep";
import { relTime } from "@/lib/format";
import { useSession } from "@/stores/session";
import type { DevicePhoto } from "@/types/domain";

/** Minimum role for every write control here (mirrors api/v2/device_photos.PHOTO_WRITE_ROLE). */
export const PHOTO_WRITE_ROLE = "site_operator" as const;

/** Upload lifecycle shown in the slot being filled. */
type UploadStage = { kind: "preparing" } | { kind: "uploading"; pct: number } | null;

/** Plain sentence for any failed mutation in this section. */
function errorText(err: unknown): string {
  return err instanceof ApiError
    ? uploadErrorMessage(err.status, err.message)
    : uploadErrorMessage(-1);
}

export function DeviceLocationPhotos({
  serial,
  description,
}: {
  serial: string;
  description: string | null;
}) {
  const canWrite = useSession((s) => s.hasRole(PHOTO_WRITE_ROLE));
  const { data, isLoading, error } = useDevicePhotos(serial);
  const photos = data?.items ?? [];
  const max = data?.max_photos ?? MAX_PHOTOS;
  const headingId = useId();
  // Index into `photos` of the photo open in the full-size viewer (null = closed).
  const [viewing, setViewing] = useState<number | null>(null);

  const unstripped = photos.filter((p) => !p.metadata_stripped).length;

  return (
    <section aria-labelledby={headingId}>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 id={headingId} className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
          Location &amp; photos
        </h3>
        {/* Capacity, always visible: the installer knows before trying a 4th photo. */}
        <span className="font-mono text-xs tabular-nums text-ink-muted" aria-label={`${photos.length} of ${max} photos`}>
          {slotCountLabel(photos.length, max)}
        </span>
      </div>

      <DescriptionField serial={serial} description={description} canWrite={canWrite} />

      {error ? (
        <p role="alert" className="mt-3 text-sm text-critical">
          Photos could not be loaded. {errorText(error)}
        </p>
      ) : (
        <PhotoStrip
          serial={serial}
          photos={photos}
          max={max}
          loading={isLoading}
          canWrite={canWrite}
          onOpen={setViewing}
        />
      )}

      {unstripped > 0 && (
        <p className="mt-2 text-xs text-ink-muted">
          {unstripped === 1 ? "1 HEIC photo" : `${unstripped} HEIC photos`} could not be checked
          for embedded location data; it is stored as uploaded.
        </p>
      )}

      <PhotoViewer
        serial={serial}
        photos={photos}
        index={viewing}
        canWrite={canWrite}
        onIndexChange={setViewing}
      />
    </section>
  );
}

/* ------------------------------------------------------------------------------------------ *
 * Installer note
 * ------------------------------------------------------------------------------------------ */

function DescriptionField({
  serial,
  description,
  canWrite,
}: {
  serial: string;
  description: string | null;
  canWrite: boolean;
}) {
  const save = useSetDeviceDescription(serial);
  const saved = description ?? "";
  const [draft, setDraft] = useState(saved);
  const [justSaved, setJustSaved] = useState(false);
  const fieldId = useId();
  const dirty = draft.trim() !== saved.trim();

  // Follow the server value (another user's save, a refetch) unless there are unsaved edits.
  useEffect(() => {
    if (!dirty) setDraft(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to the server value
  }, [saved]);

  if (!canWrite) {
    return saved ? (
      <p className="whitespace-pre-wrap break-words text-sm text-ink">{saved}</p>
    ) : (
      <p className="text-sm text-ink-subtle">No description yet.</p>
    );
  }

  const left = charsLeft(draft, DESCRIPTION_MAX);
  const submit = () => {
    if (!dirty || left < 0) return;
    setJustSaved(false);
    save.mutate(draft.trim() ? draft : null, { onSuccess: () => setJustSaved(true) });
  };

  return (
    <div>
      <label htmlFor={fieldId} className="sr-only">
        Description
      </label>
      <textarea
        id={fieldId}
        rows={3}
        value={draft}
        maxLength={DESCRIPTION_MAX}
        onChange={(e) => {
          setDraft(e.target.value);
          setJustSaved(false);
        }}
        onKeyDown={(e) => {
          // Ctrl/Cmd+Enter saves from a keyboard; a plain Enter is a line break in a note.
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="Where is it and how is it mounted? e.g. Basement, behind the TV, ceiling mount"
        className="block w-full resize-y rounded border border-line bg-surface px-3 py-2 text-base text-ink outline-none placeholder:text-ink-subtle focus:border-brand sm:text-sm"
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button variant="primary" disabled={!dirty || left < 0 || save.isPending} onClick={submit}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        {dirty && (
          <Button variant="ghost" onClick={() => setDraft(saved)} disabled={save.isPending}>
            Discard
          </Button>
        )}
        <span aria-live="polite" className="text-xs text-ink-muted">
          {save.isError ? null : justSaved && !dirty ? "Saved." : null}
        </span>
        <span
          className={`ml-auto font-mono text-xs tabular-nums ${left < 50 ? "text-ink" : "text-ink-subtle"}`}
        >
          {draft.length} / {DESCRIPTION_MAX}
        </span>
      </div>
      {save.isError && (
        <p role="alert" className="mt-1 text-sm text-critical">
          {errorText(save.error)}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------ *
 * The 3-slot strip
 * ------------------------------------------------------------------------------------------ */

function PhotoStrip({
  serial,
  photos,
  max,
  loading,
  canWrite,
  onOpen,
}: {
  serial: string;
  photos: DevicePhoto[];
  max: number;
  loading: boolean;
  canWrite: boolean;
  onOpen: (index: number) => void;
}) {
  const upload = useUploadDevicePhoto(serial);
  const [stage, setStage] = useState<UploadStage>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const busy = stage !== null;

  /**
   * The single upload routine for BOTH entry points — a chosen file and a live camera still
   * (the camera wraps its JPEG blob in a File, so this path is byte-for-byte the same):
   * pre-check → downscale/re-encode/EXIF-strip → size ceiling → upload with progress.
   */
  const processFile = (file: File) => {
    const problem = precheckPhotoFile(file);
    if (problem) {
      setUploadError(problem);
      return;
    }
    setUploadError(null);
    setStage({ kind: "preparing" });
    void (async () => {
      const blob = await prepareForUpload(file);
      if (uploadTooLarge(blob.size)) {
        setStage(null);
        setUploadError("Photo is larger than 10 MB.");
        return;
      }
      setStage({ kind: "uploading", pct: 0 });
      upload.mutate(
        {
          blob,
          filename: blob === file ? file.name : reencodedName(file.name),
          onProgress: (loaded, total) =>
            setStage({ kind: "uploading", pct: progressPercent(loaded, total) }),
        },
        {
          onSuccess: () => setStage(null),
          onError: (err) => {
            setStage(null);
            setUploadError(errorText(err));
          },
        },
      );
    })();
  };

  // Slot layout: filled photos first (server slot order), then the upload-in-progress tile,
  // then the Add tile (writers, room left), then empty placeholders up to `max`.
  const tiles: ReactNode[] = photos.map((p, i) => (
    <PhotoTile key={p.id} photo={p} onOpen={() => onOpen(i)} />
  ));
  if (busy) tiles.push(<ProgressTile key="progress" stage={stage} />);
  if (canWrite && !busy && photos.length < max) {
    tiles.push(
      <AddPhoto
        key="add"
        used={photos.length}
        max={max}
        onFile={processFile}
      />,
    );
  }
  while (tiles.length < max) {
    tiles.push(
      <div
        key={`empty-${tiles.length}`}
        aria-hidden="true"
        className="aspect-[4/3] rounded-lg border border-dashed border-line/70 bg-surface-2/60"
      />,
    );
  }

  return (
    <div className="mt-3">
      <div className={`grid grid-cols-3 gap-2 ${loading ? "animate-pulse" : ""}`}>{tiles}</div>
      {!loading && photos.length === 0 && !canWrite && (
        <p className="mt-2 text-sm text-ink-subtle">No installation photos.</p>
      )}
      {uploadError && (
        <p role="alert" className="mt-2 text-sm text-critical">
          {uploadError}
        </p>
      )}
    </div>
  );
}

/**
 * The "Add photo" slot. Tapping it opens a chooser (Take photo / Choose file) when a live
 * camera is usable; otherwise it goes straight to the file input, so a desktop or an older
 * browser behaves exactly as before. The hidden file input is the fallback the camera modal
 * hands control back to (permission denied / no camera / camera busy).
 */
function AddPhoto({
  used,
  max,
  onFile,
}: {
  used: number;
  max: number;
  onFile: (file: File) => void;
}) {
  const [chooserOpen, setChooserOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Probe once: getUserMedia present AND a secure context. (Guarding SSR/tests where there is
  // no `navigator`; in the field over HTTPS this is true and the chooser is shown.)
  const cameraSupported = useMemo(
    () =>
      typeof navigator !== "undefined" &&
      cameraCaptureSupported({
        mediaDevices: navigator.mediaDevices,
        isSecureContext: typeof window !== "undefined" ? window.isSecureContext : undefined,
      }),
    [],
  );

  const openFilePicker = () => inputRef.current?.click();

  const onTileClick = () => {
    if (cameraSupported) setChooserOpen(true);
    else openFilePicker(); // no live camera: same one-tap file flow as before
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again (after an error) still fires onChange.
    e.target.value = "";
    if (file) onFile(file);
  };

  return (
    <>
      <button
        type="button"
        onClick={onTileClick}
        aria-label={`Add a photo (${slotCountLabel(used, max)} used)`}
        aria-haspopup={cameraSupported ? "dialog" : undefined}
        className="group relative flex aspect-[4/3] min-h-touch cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-line bg-surface text-ink-muted outline-none transition-colors hover:border-brand hover:text-brand focus-visible:border-brand focus-visible:text-brand focus-visible:ring-2 focus-visible:ring-brand/40"
      >
        <CameraIcon />
        <span className="text-xs font-medium">Add photo</span>
      </button>

      {/* The fallback file input (capture="environment" opens the rear camera on the OS picker). */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={onPick}
        aria-hidden="true"
        tabIndex={-1}
      />

      <PhotoChooser
        open={chooserOpen}
        onOpenChange={setChooserOpen}
        onTakePhoto={() => {
          setChooserOpen(false);
          setCameraOpen(true);
        }}
        onChooseFile={() => {
          setChooserOpen(false);
          openFilePicker();
        }}
      />

      <DeviceCameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={onFile}
        onFallbackToFile={openFilePicker}
      />
    </>
  );
}

/** The two-way chooser sheet: a live camera or the file picker. */
function PhotoChooser({
  open,
  onOpenChange,
  onTakePhoto,
  onChooseFile,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTakePhoto: () => void;
  onChooseFile: () => void;
}) {
  const titleId = useId();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-toast bg-ink/60" />
        {/* Bottom sheet on phones, centred card from `sm` up — big touch targets either way. */}
        <Dialog.Content
          aria-labelledby={titleId}
          aria-describedby={undefined}
          className="md-dialog fixed inset-x-0 bottom-0 z-toast rounded-t-xl bg-surface p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-sheet outline-none sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-80 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl"
        >
          <Dialog.Title id={titleId} className="text-sm font-semibold text-ink">
            Add a photo
          </Dialog.Title>
          <div className="mt-3 flex flex-col gap-2">
            <Button variant="primary" className="w-full" onClick={onTakePhoto}>
              <CameraIcon />
              Take photo
            </Button>
            <Button variant="secondary" className="w-full" onClick={onChooseFile}>
              Choose file
            </Button>
            <Dialog.Close asChild>
              <Button variant="ghost" className="w-full">
                Cancel
              </Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** A filled slot: the thumbnail (or a HEIC placeholder) with its caption along the bottom. */
function PhotoTile({ photo, onOpen }: { photo: DevicePhoto; onOpen: () => void }) {
  const label = photo.caption || `Installation photo ${photo.slot}`;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative aspect-[4/3] overflow-hidden rounded-lg border border-line bg-surface-2 outline-none focus-visible:ring-2 focus-visible:ring-brand"
      aria-label={`Open ${label}`}
    >
      {photo.has_thumbnail ? (
        <img
          src={photo.thumbnail_url}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
        />
      ) : (
        <span className="flex h-full w-full items-center justify-center font-mono text-xs text-ink-muted">
          HEIC
        </span>
      )}
      {photo.caption && (
        <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-ink/80 to-transparent px-1.5 pb-1 pt-3 text-left text-[0.6875rem] leading-tight text-ink-invert">
          {photo.caption}
        </span>
      )}
    </button>
  );
}

/** The slot being filled: preparing (indeterminate) or uploading (percent). */
function ProgressTile({ stage }: { stage: UploadStage }) {
  const pct = stage?.kind === "uploading" ? stage.pct : null;
  return (
    <div
      role="status"
      className="flex aspect-[4/3] flex-col items-center justify-center gap-2 rounded-lg border border-line bg-surface-2 px-2"
    >
      <span className="text-xs font-medium text-ink">
        {pct === null ? "Preparing…" : `Uploading ${pct}%`}
      </span>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-line">
        <div
          className={`h-full rounded-full bg-brand transition-[width] duration-150 ${pct === null ? "w-1/3 animate-pulse" : ""}`}
          style={pct === null ? undefined : { width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------ *
 * Full-size viewer
 * ------------------------------------------------------------------------------------------ */

function PhotoViewer({
  serial,
  photos,
  index,
  canWrite,
  onIndexChange,
}: {
  serial: string;
  photos: DevicePhoto[];
  index: number | null;
  canWrite: boolean;
  onIndexChange: (index: number | null) => void;
}) {
  const photo = index !== null ? photos[index] : undefined;
  const open = photo !== undefined;
  const count = photos.length;
  const go = (delta: number) => {
    if (index === null || count < 2) return;
    onIndexChange((index + delta + count) % count);
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onIndexChange(null)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-toast bg-ink/90" />
        <Dialog.Content
          className="fixed inset-0 z-toast flex flex-col outline-none"
          aria-describedby={undefined}
          onKeyDown={(e) => {
            // Arrow keys page through photos unless the caption field has focus.
            if ((e.target as HTMLElement).tagName === "INPUT") return;
            if (e.key === "ArrowRight") go(1);
            if (e.key === "ArrowLeft") go(-1);
          }}
        >
          {photo && (
            <>
              <div className="flex items-center justify-between gap-2 px-4 py-2 text-ink-invert">
                <Dialog.Title className="text-sm font-medium">
                  Photo {photo.slot} of {MAX_PHOTOS}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <Button variant="ghost" className="text-ink-invert hover:bg-ink-invert/10" aria-label="Close photo">
                    ✕
                  </Button>
                </Dialog.Close>
              </div>

              <div className="relative flex min-h-0 flex-1 items-center justify-center px-2">
                <FullImage key={photo.id} photo={photo} />
                {count > 1 && (
                  <>
                    <NavButton side="left" label="Previous photo" onClick={() => go(-1)} />
                    <NavButton side="right" label="Next photo" onClick={() => go(1)} />
                  </>
                )}
              </div>

              <PhotoDetails
                key={photo.id}
                serial={serial}
                photo={photo}
                canWrite={canWrite}
                onDeleted={() => onIndexChange(null)}
              />
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** The stored image, with an honest fallback when this browser cannot render it (HEIC). */
function FullImage({ photo }: { photo: DevicePhoto }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="max-w-sm text-center text-sm text-ink-invert">
        <p>This browser can’t display this photo ({photo.content_type.replace("image/", "").toUpperCase()}).</p>
        <a
          href={photo.image_url}
          target="_blank"
          rel="noopener"
          className="mt-2 inline-flex min-h-touch items-center font-medium underline underline-offset-4"
        >
          Open the original
        </a>
      </div>
    );
  }
  return (
    <img
      src={photo.image_url}
      alt={photo.caption || `Installation photo ${photo.slot}`}
      onError={() => setFailed(true)}
      className="max-h-full max-w-full select-none object-contain"
    />
  );
}

function NavButton({ side, label, onClick }: { side: "left" | "right"; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`absolute top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-ink/50 text-xl text-ink-invert outline-none hover:bg-ink/70 focus-visible:ring-2 focus-visible:ring-brand ${side === "left" ? "left-3" : "right-3"}`}
    >
      {side === "left" ? "‹" : "›"}
    </button>
  );
}

/** Bottom panel: caption (editable for writers), facts, and delete-with-confirm. */
function PhotoDetails({
  serial,
  photo,
  canWrite,
  onDeleted,
}: {
  serial: string;
  photo: DevicePhoto;
  canWrite: boolean;
  onDeleted: () => void;
}) {
  const setCaption = useSetPhotoCaption(serial);
  const remove = useDeleteDevicePhoto(serial);
  const [caption, setCaptionDraft] = useState(photo.caption ?? "");
  const [confirming, setConfirming] = useState(false);
  const captionId = useId();
  const captionDirty = caption.trim() !== (photo.caption ?? "").trim();

  const facts = [
    photo.uploaded_by ? `Added by ${photo.uploaded_by}` : null,
    photo.created_at ? relTime(photo.created_at) : null,
    photo.width && photo.height ? `${photo.width}×${photo.height}` : null,
    formatBytes(photo.size_bytes),
  ].filter(Boolean);

  return (
    <div className="rounded-t-xl bg-overlay px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 shadow-sheet">
      <div className="mx-auto flex max-w-2xl flex-col gap-3">
        {canWrite ? (
          <div>
            <label htmlFor={captionId} className="text-xs text-ink-muted">
              Caption
            </label>
            <div className="mt-1 flex gap-2">
              <input
                id={captionId}
                value={caption}
                maxLength={CAPTION_MAX}
                onChange={(e) => setCaptionDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && captionDirty) setCaption.mutate({ id: photo.id, caption: caption.trim() || null });
                }}
                placeholder="e.g. Ceiling tile above the TV, looking north"
                className="min-h-touch min-w-0 flex-1 rounded border border-line bg-surface px-3 text-base text-ink outline-none placeholder:text-ink-subtle focus:border-brand sm:text-sm"
              />
              <Button
                variant="primary"
                disabled={!captionDirty || setCaption.isPending}
                onClick={() => setCaption.mutate({ id: photo.id, caption: caption.trim() || null })}
              >
                {setCaption.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
            {setCaption.isError && (
              <p role="alert" className="mt-1 text-sm text-critical">{errorText(setCaption.error)}</p>
            )}
          </div>
        ) : (
          photo.caption && <p className="text-sm text-ink">{photo.caption}</p>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <p className="text-xs text-ink-muted">{facts.join(" · ")}</p>
          {canWrite && !confirming && (
            <Button variant="secondary" className="ml-auto" onClick={() => setConfirming(true)}>
              Delete photo
            </Button>
          )}
        </div>

        {canWrite && confirming && (
          <div role="alertdialog" aria-label="Confirm delete" className="flex flex-wrap items-center gap-2 rounded border border-critical/40 bg-critical-soft p-3">
            <span className="text-sm text-critical">Delete this photo? This can’t be undone.</span>
            <div className="ml-auto flex gap-2">
              <Button variant="ghost" onClick={() => setConfirming(false)} disabled={remove.isPending}>
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={remove.isPending}
                onClick={() => remove.mutate(photo.id, { onSuccess: onDeleted })}
              >
                {remove.isPending ? "Deleting…" : "Delete"}
              </Button>
            </div>
            {remove.isError && (
              <p role="alert" className="w-full text-sm text-critical">{errorText(remove.error)}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Simple outline camera glyph (inherits currentColor). */
function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path strokeLinejoin="round" d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2.1l1.4-2h6l1.4 2h2.1A1.5 1.5 0 0 1 20 8.5v9A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5z" />
      <circle cx="12" cy="13" r="3.25" />
      <path strokeLinecap="round" d="M12 10.5v5M9.5 13h5" className="opacity-0 group-hover:opacity-100" />
    </svg>
  );
}
